"use client";

import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { Flex, FlexColumn, Media, TextBody, TextHeading } from "@opensea/ui-kit";
import { useTranslations } from "next-intl";
import { useInView } from "react-intersection-observer";
import { useShallow } from "zustand/react/shallow";
import { DropStatus } from "@/components/collection/drops/constants";
import { PreviewImagesPreview } from "@/components/collection/overview/components/CollectionOverviewPreview/components/PreviewImagesPreview";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { mintModuleContentClasses } from "../../styles";
import { useDropStore } from "../CollectionDropContext";
import { MintCTAModule, MintCTAModuleFragment } from "../MintCTAModule";
import { MintFeed } from "../MintFeed";
import { MintOptionsModule } from "../MintOptionsModule";
import { MintProgress } from "../MintProgress";
import { MintSchedule } from "../MintSchedule";
import { PreviewImages } from "../PreviewImages";
import { SalesFeed } from "../SalesFeed";
import { MintModuleFragment } from "./MintModule.fragment";
import { MintModuleSkeleton } from "./MintModuleSkeleton";
const query = graphql(`
    query MintModuleQuery($collectionSlug: String!) {
      dropBySlug(slug: $collectionSlug) {
        __typename
        ...MintCTAModule
      }
    }
  `, [MintCTAModuleFragment]);
export const MintModule = withSuspense(function MintModule({
  collection: collectionKey,
  isPreview
}) {
  const collection = readFragment(MintModuleFragment, collectionKey);
  const [{
    data
  }] = useQuery({
    query,
    variables: {
      collectionSlug: collection?.slug ?? ""
    },
    pause: !collection
  });
  const hasPreviewImages = Boolean(collection?.about?.previewMedia?.length);
  const [is1155Drop, status, setShowBottomBar] = useDropStore(useShallow(state => [state.is1155Drop, state.status, state.setShowBottomBar]));
  const t = useTranslations("MintModule");
  const [observerRef] = useInView({
    threshold: 0,
    onChange: inView => {
      setShowBottomBar(!inView);
    }
  });
  const feeds = <div className="mt-6 flex flex-col gap-6 lg:mt-10 lg:grid lg:grid-cols-12 lg:gap-10">
        <FlexColumn className="h-[220px] lg:col-span-6 lg:h-[440px]">
          <TextBody className="font-mono uppercase" weight="semibold">
            {t("liveMints")}
          </TextBody>
          <MintFeed />
        </FlexColumn>
        <FlexColumn className="h-[220px] lg:col-span-6 lg:h-[440px]">
          <TextBody className="font-mono uppercase" weight="semibold">
            {t("liveSales")}
          </TextBody>
          <SalesFeed />
        </FlexColumn>
      </div>;
  const previewImages = isPreview ? <PreviewImagesPreview /> : <PreviewImages collection={collection} />;
  if (!(collection && data?.dropBySlug)) {
    return null;
  }
  return <>
        <Flex className="my-7 grid w-full grid-cols-12 justify-center gap-8">
          {hasPreviewImages || isPreview ? <Media greaterThanOrEqual="lg">
              <div className="col-span-5">{previewImages}</div>
            </Media> : <div className="col-span-2" />}
          <FlexColumn className={mintModuleContentClasses({
        hasPreviewImages: hasPreviewImages || isPreview
      })}>
            <TextHeading size="lg">
              {t("mint", {
            collectionName: collection.name
          })}
            </TextHeading>
            <MintProgress />
            <Media lessThan="lg">{previewImages}</Media>
            <div ref={observerRef}>
              {is1155Drop && status === DropStatus.MINTING ? <MintOptionsModule isPreview={isPreview} /> : <MintCTAModule drop={data.dropBySlug} isPreview={isPreview} />}
            </div>
            <div className="mt-4">
              <MintSchedule />
            </div>
            <Media greaterThanOrEqual="xl">{feeds}</Media>
          </FlexColumn>
        </Flex>
        <Media lessThan="xl">{feeds}</Media>
      </>;
}, {
  fallback: <MintModuleSkeleton />
});